import React, { ReactElement, useState, useContext } from "react";
import { Box } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineFileSearch } from "react-icons/ai";
import { BsPersonCheck } from "react-icons/bs";
import { MdOutlineContentCopy } from "react-icons/md";

import ReceiverResendKYCConfirmModal from "./ReceiverResendKYCConfirmModal";
import SweetAlert2 from "react-sweetalert2";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { ProfileContext } from "../../../../store/ProfileProvider";

dayjs.extend(utc);
dayjs.extend(timezone);

type Merchant = {
  id: string;
  name: string;
};

type Subaccount = {
  id: string;
  name: string;
};

type RowData = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  reference: string;
  status: string;
  kyc_status: string;
  created_at: string;
  merchant: Merchant;
  subaccount: Subaccount;
};

const ReceiverList: React.FC<{
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
  triggerReloadList(): void;
}> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
  triggerReloadList,
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [isOpenResendKYCConfirmModal, setIsOpenResendKYCConfirmModal] =
    useState<boolean>(false);
  const [receiverDetailForResendKYC, setReceiverDetailForResendKYC] =
    useState(null);
  const [swalProps, setSwalProps] = useState<any>({});
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const handleClickCopy = async (payoutId: string) => {
    try {
      await navigator.clipboard.writeText(payoutId);
    } catch (err) {
      console.error('Failed to copy payout ID: ', err);
    }
  };

  const columns: GridColDef[] = [
      {
        field: "id",
        headerName: "",
        flex: 1,
        minWidth: 50,
        maxWidth: 50,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params: GridRenderCellParams<RowData, string>) =>
          <Tooltip title="Copy Receiver ID" arrow placement="top">
            <div className="">
              <IconButton
                id={"button-copy-receiverid-" + params?.row?.id}
                onClick={() => handleClickCopy(params.row.id)}
              >
                <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
              </IconButton>
            </div>
          </Tooltip>
      },
    {
      field: "created_at",
      headerName: "Created Date",
      flex: 1,
      minWidth: 175,
      sortable: false,
      renderCell: (params: GridRenderCellParams<RowData, string>) =>
        formatDate(params.row.created_at),
    },
    {
      field: "subaccount",
      headerName: "Subaccount",
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
        params.row.subaccount.name,
    },
    {
      field: "first_name",
      headerName: "First Name",
      flex: 1,
      minWidth: 160,
      sortable: false,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      flex: 1,
      minWidth: 160,
      sortable: false,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 250,
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<RowData, any, string>) => {
        const customerStatus = params.value as string;
        const statusItem = statusList.find(
          (item) => item.status === customerStatus
        );
        return statusItem ? statusItem.display : null;
      },
    },
    {
      field: "kyc_status",
      headerName: "KYC Status",
      flex: 1,
      minWidth: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<RowData, any, string>) => {
        const kycStatus = params.value as string;
        const statusItem = kycStatusList.find((item) => item.status === kycStatus);
        return statusItem ? statusItem.display : null;
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      flex: 1,
      minWidth: 140,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        return (
          <>
            {(params?.row?.kyc_status === "pending" ||
              params?.row?.kyc_status === "failed") &&
              userRole === "superadmin" && (
                <Tooltip title="Resend KYC Link" arrow placement="top">
                  <IconButton
                    id={"button-resend-kyc-" + params?.row?.id}
                    onClick={() => handleResendKYC(params.row.id)}
                  >
                    <BsPersonCheck className="h-6 w-6 text-navy-700 opacity-90" />
                  </IconButton>
                </Tooltip>
              )}
            <Tooltip title="View More Details" arrow placement="top">
              <div className="mr-5">
                <IconButton
                  component={Link}
                  to={`/${userRole}/receiver/${params.row.id}`}
                  id={"button-detail-" + params?.row?.id}
                >
                  <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
                </IconButton>
              </div>
            </Tooltip>
          </>
        );
      },
    },
  ];

  interface CustomerStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: CustomerStatusItems[] = [
    {
      status: "creating",
      value: "creating",
      display: (
        <Badge className={`${statusClasses} status-initializing`}>Creating</Badge>
      ),
    },
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "completed",
      value: "completed",
      display: (
        <Badge className={`${statusClasses} status-completed`}>Completed</Badge>
      ),
    },
    {
      status: "active",
      value: "active",
      display: (
        <Badge className={`${statusClasses} status-active`}>Active</Badge>
      ),
    },
    {
      status: "inactive",
      value: "inactive",
      display: (
        <Badge className={`${statusClasses} status-inactive`}>Inactive</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
  ];

  const kycStatusList: CustomerStatusItems[] = [
    {
      status: "creating",
      value: "creating",
      display: (
        <Badge className={`${statusClasses} status-initializing`}>Creating</Badge>
      ),
    },
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "completed",
      value: "completed",
      display: (
        <Badge className={`${statusClasses} status-completed`}>Completed</Badge>
      ),
    },
    {
      status: "active",
      value: "active",
      display: (
        <Badge className={`${statusClasses} status-active`}>Active</Badge>
      ),
    },
    {
      status: "inactive",
      value: "inactive",
      display: (
        <Badge className={`${statusClasses} status-inactive`}>Inactive</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
  ];

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start",
    row: "",
  };

  const filterColumns = () => {
    let rebuildColumns = [];
    if (userRole !== "superadmin") {
      const excludeFields = ["subaccount"];
      rebuildColumns = columns.filter(
        (column) => !!!excludeFields.includes(column?.field)
      );
    } else {
      rebuildColumns = columns;
    }
    return rebuildColumns;
  };

  const handleResendKYC = (id: any) => {
    const receiver = tableData.filter((x: any) => x.id === id)[0];
    setReceiverDetailForResendKYC(receiver);
    showResendKYCConfirmModal();
  };

  const showResendKYCConfirmModal = () => {
    setIsOpenResendKYCConfirmModal(true);
  };

  const closeResendKYCConfirmModal = () => {
    setReceiverDetailForResendKYC(null);
    setIsOpenResendKYCConfirmModal(false);
  };

  return (
    <div>
      {isOpenResendKYCConfirmModal && (
        <ReceiverResendKYCConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenResendKYCConfirmModal}
          receiverToResendKYC={receiverDetailForResendKYC}
          onClose={closeResendKYCConfirmModal}
        />
      )}

      <Box className="card mt-3 w-full">
        <DataGrid
          sx={{
            "& .MuiDataGrid-cell": {
              padding: "8px 16px",
            },
            "& .MuiDataGrid-columnHeader": {
              padding: "8px 16px",
            },
            "& .MuiTablePagination-input": {
              marginRight: "-20px",
            },
            "& .MuiTablePagination-displayedRows": {
              display: "none",
            },
          }}
          loading={isLoading}
          rows={tableData || []}
          columns={filterColumns()}
          getRowId={(row) => row.id}
          rowCount={rowCount}
          sortingMode="server"
          paginationMode="server"
          pageSizeOptions={[10, 25, 50, 100]}
          rowHeight={48}
          columnHeaderHeight={48}
          paginationModel={{ page: page, pageSize: pageSize }}
          onPaginationModelChange={(newValue) => setPaginationModel(newValue)}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          classes={customDataGridStyles}
          hideFooter={currentLastKey === "" && page === 0}
          autoHeight
          localeText={{
            noRowsLabel: "No data available",
          }}
        />
      </Box>
      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </div>
  );
};

export default ReceiverList;
